/**
 * @file api-wms.ts
 * @description Admin API-клиент WMS (phase13.0; контракт переведён на SKU в
 * phase19.13 этап 0).
 *
 * Складской учёт ведётся по фасовке (`skuId`), а не по товару: на складе лежат
 * «Чабрец 50 г» и «Чабрец 100 г» как разные позиции. `productId` остался только
 * фильтром «все фасовки товара» в списках.
 *
 * Сюда же входит пикер фасовок `GET /admin/wms/skus` — складской, без цен и без
 * прав на заказы (phase19.13, финальный батч).
 */

import {
  apiDownload,
  apiFetch,
  apiFetchWithMeta,
  type ApiFileResponse,
} from "./api-client";
import type { LabelTemplate, LotLabelStatus } from "./api-labels";
import type { WarehouseKind } from "./wms-warehouse-kind";

// ─── Types ────────────────────────────────────────────────────────────────────

export type WarehouseProductKind =
  | "RAW_MATERIAL"
  | "SEMI_FINISHED"
  | "FINISHED_GOOD"
  | "KIT";

export type SalesChannelKind =
  | "RETAIL"
  | "WEBSITE"
  | "OZON"
  | "WILDBERRIES"
  | "AVITO"
  | "B2B";

export type LotSourceType = "PURCHASE" | "PRODUCTION" | "RETURN" | "ADJUSTMENT";

export type CrptStatus = "NOT_REQUIRED" | "PENDING" | "ACTIVE" | "WITHDRAWN";

export type StockMovementKind =
  | "RECEIVE"
  | "SALE"
  | "RESERVE"
  | "UNRESERVE"
  | "WRITEOFF"
  | "TRANSFER_IN"
  | "TRANSFER_OUT"
  | "RETURN_TO_STOCK"
  | "ADJUST"
  | "PRODUCTION_CONSUMPTION"
  | "PRODUCTION_OUTPUT";

export type WriteoffReason =
  | "DAMAGED"
  | "EXPIRED"
  | "LOST"
  | "RETURN_TO_VENDOR"
  /** phase19.22 (W17-9b) — сырьё ушло в производство; пара к приёмке «внутренний выпуск». */
  | "PRODUCTION_INPUT"
  | "OTHER";

/** phase19.22 (W17-9b, W19) — причина приёмки; от неё зависит происхождение партии. */
export type GoodsReceiptReason = "PURCHASE" | "INTERNAL_OUTPUT" | "OPENING_BALANCE";

export type StockReservationStatus =
  | "PENDING"
  | "CONFIRMED"
  | "RELEASED"
  | "FULFILLED";

export type WmsDeviceKind =
  | "PRINTER_LABEL"
  | "PRINTER_RECEIPT"
  | "PRINTER_DOCUMENT"
  | "SCANNER_USB"
  | "SCANNER_CAMERA"
  | "SCALE"
  | "CASH_REGISTER"
  | "PAYMENT_TERMINAL"
  | "POS_TERMINAL"
  | "OTHER";

export type WmsDeviceTransport =
  | "USB"
  | "NETWORK"
  | "AGENT"
  | "BLUETOOTH"
  | "SYSTEM"
  | "CLOUD_API";

export interface WarehouseDto {
  id: string;
  shopId: string;
  name: string;
  code: string;
  address: string | null;
  /** phase19 — телефон точки (блок самовывоза checkout). */
  phone: string | null;
  /**
   * phase19.13 — график работы точки свободным текстом (≤200 символов).
   * Показывается клиенту в ЛК и в письме о готовности заказа к выдаче.
   */
  workingHours: string | null;
  /** phase19.14 (W8) — назначение склада; от него зависит «доступно». */
  kind: WarehouseKind;
  /**
   * phase19.14 (W8) — остатки склада попадают в «доступно» и резервируются.
   * У карантина и транзита сервер снимает флаг принудительно.
   */
  isSellable: boolean;
  isDefault: boolean;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  settings: WarehouseSettingsDto | null;
}

export interface WarehouseSettingsDto {
  id: string;
  warehouseId: string;
  defaultFefo: boolean;
  crptRequired: boolean;
  expiryAlertDays: number;
  expiryCriticalDays: number;
  reservationGuestTtlMin: number;
  reservationConfirmedTtlHrs: number;
  receiptNumberFormat: string;
  writeoffNumberFormat: string;
  /**
   * phase19.13 шаг 8 — разрешать остатку уходить в минус при продаже/упаковке.
   * Дефолт true; строгий режим (false) блокирует операцию при нехватке остатка.
   */
  allowNegativeStock: boolean;
  /**
   * phase19.14 (W14) — требовать скан ячейки при сборке. Флаг хранится уже
   * сейчас, сам скан-гвард появится в phase19.22.
   */
  requireBinScanOnPick: boolean;
  /**
   * phase19.24 — через сколько дней черновик документа считается зависшим и
   * попадает в «Требует внимания» на дашборде склада. Целое 1..90, дефолт 3.
   */
  staleDraftDays: number;
  /**
   * phase19.22 (W17-5a) — принтер этикеток по умолчанию (`PRINTER_LABEL` этого
   * склада). Браузерную печать не направляет — «для журнала и терморежима».
   */
  defaultLabelPrinterId?: string | null;
}

export interface DeviceConfigDto {
  id: string;
  warehouseId: string;
  kind: WmsDeviceKind;
  transport: WmsDeviceTransport;
  name: string;
  vendor: string | null;
  modelName: string | null;
  config: Record<string, unknown> | null;
  isDefault: boolean;
  isActive: boolean;
  lastSeenAt: string | null;
  notes: string | null;
}

/**
 * Товар внутри ссылки на фасовку. Состав полей зависит от выборки на сервере
 * (в партиях нет `slug`, в приёмке есть `trackByLot`) — поэтому всё, кроме
 * идентификатора и названия, опционально.
 */
export interface WmsSkuProductRef {
  id: string;
  slug?: string;
  displayName: string | null;
  warehouseKind?: WarehouseProductKind | null;
  trackByLot?: boolean;
}

/**
 * phase19.22 — описание печати фасовки (`apps/api/src/wms/sku-print-info.ts`,
 * docs/api/wms-warehouses.md): сервер вливает его в `sku` строк приёмки и
 * остатков, чтобы диалог печати не спрашивал шаблон по каждой фасовке.
 */
export interface SkuPrintInfo {
  /** Действующий шаблон: фасовка ?? вид товара ?? STANDARD. */
  effectiveLabelTemplate: LabelTemplate;
  labelTemplateSource: "sku" | "kind" | "default";
  /** Содержимое фасовки: «100». */
  saleQuantity: string;
  saleUnit: { code: string; label: string } | null;
  /** Единица счёта строки: «шт» у пачек, «г»/«кг» у развеса. */
  countUnit: string;
  /** Развес: этикетка на тару, а не на единицу (W21). */
  isMeasured: boolean;
}

/**
 * Ссылка на фасовку (SKU) в ответах WMS: артикул + карточка товара.
 * Поля `SkuPrintInfo` опциональны: их отдают только приёмка и остатки
 * (и старый сервер — нигде).
 */
export interface WmsSkuRef extends Partial<SkuPrintInfo> {
  id: string;
  code: string;
  displayName: string | null;
  /** phase19.22 (W17-1) — код склада `CHB…`; приходит в строках приёмки. */
  internalCode?: string;
  product: WmsSkuProductRef;
}

export interface StockLevelDto {
  id: string;
  shopId: string;
  skuId: string;
  warehouseId: string;
  lotId: string | null;
  quantity: string;
  totalReserved: string;
  updatedAt: string;
  sku: WmsSkuRef;
  warehouse: { id: string; name: string; code: string };
  /**
   * phase19.23 — сервер отдаёт и ячейку размещения партии (`lot.bin`):
   * в остатках и в диалоге «Положить товар» сразу видно, откуда придётся
   * переносить партию. `null` — адрес не задан.
   */
  lot:
    | {
        id: string;
        batchNumber: string;
        expiresAt: string | null;
        bin?: { id: string; code: string; label: string | null } | null;
        /** phase19.22 — код партии для скана и признаки этикеток (W17-9c, W20). */
        code?: string;
        labelStatus?: LotLabelStatus;
        labelsPrintedAt?: string | null;
        expiryRequired?: boolean;
      }
    | null;
}

/**
 * Ответ реестра остатков: строки плюс признак «сервер показал не всё»
 * (правка по ревью 2026-09-20).
 *
 * `truncated` приходит в `meta` — сервер режет тяжёлые выборки (в первую
 * очередь `belowMin` по всем складам). Без этого признака экран честно писал
 * «Позиций в отборе: 500» и врал: строк на складе было больше.
 */
export interface StockLevelsResult {
  items: StockLevelDto[];
  /** Сервер обрезал выборку — показаны не все строки отбора. */
  truncated: boolean;
}

/** Остаток внутри карточки партии — сервер отдаёт только склад и количество. */
export interface LotStockLevelDto {
  id: string;
  warehouseId: string;
  quantity: string;
  totalReserved: string;
  warehouse: { id: string; name: string };
}

export interface LotDto {
  id: string;
  shopId: string;
  skuId: string;
  batchNumber: string;
  lotNumber: string | null;
  manufacturedAt: string | null;
  expiresAt: string | null;
  sourceType: LotSourceType;
  supplierId: string | null;
  actualCost: string | null;
  currency: string;
  gtin: string | null;
  serialNumber: string | null;
  crptStatus: CrptStatus;
  crptInfo: Record<string, unknown> | null;
  /** phase19.14 (W14) — ячейка размещения партии; null — адрес не задан. */
  binId: string | null;
  sku?: WmsSkuRef;
  supplier?: { id: string; displayName: string } | null;
  stockLevels?: LotStockLevelDto[];
}

export interface GoodsReceiptDto {
  id: string;
  shopId: string;
  warehouseId: string;
  supplierId: string | null;
  vendorDocNumber: string | null;
  vendorDocDate: string | null;
  receiptNumber: string;
  status: "DRAFT" | "COMMITTED";
  committedAt: string | null;
  notes: string | null;
  createdAt: string;
  warehouse?: { id: string; name: string };
  supplier?: { id: string; displayName: string } | null;
  receivedBy?: { id: string; displayName: string };
  items?: GoodsReceiptItemDto[];
  /**
   * phase19.23 этап B (W14) — сколько партий приёмки ещё без адреса хранения.
   * Приходит только в карточке (`GET /admin/wms/receipts/:id`); опционально —
   * старый сервер поля не отдаёт, тогда подсказку о размещении не показываем.
   */
  unplacedLotsCount?: number;
  /**
   * phase19.24 (решение пользователя 2026-09-20) — сколько позиций приёмки
   * осталось без партии: такие остатки лист размещения разложить не может
   * (партия появится у новых приёмок, старые уедут в ячейки на W18).
   * Опционально — переходный период, пока серверный контракт не выкатан.
   */
  itemsWithoutLot?: number;
  /** phase19.22 (W17-9b) — причина приёмки; старый сервер поля не отдаёт. */
  reason?: GoodsReceiptReason;
  /** phase19.22 — парное списание «в производство» (только внутренний выпуск). */
  parentWriteoffId?: string | null;
  parentWriteoff?: {
    id: string;
    writeoffNumber: string;
    status: "DRAFT" | "COMMITTED";
    reason: WriteoffReason;
  } | null;
  /**
   * phase19.22 (W20) — id строк, чья партия будет/стала «срок не указан»:
   * у черновика — предупреждение до проведения, у проведённой — бейдж.
   */
  missingExpiryItems?: string[];
}

/**
 * Пункт предпроверки проведения приёмки. Срок (W20) — предупреждение, не
 * блок; парное списание внутреннего выпуска — БЛОК (решение пользователя
 * 2026-09-26): сервер отдаёт его в `errors[]` или с пометкой `blocking`.
 */
export interface ReceiptWarning {
  code:
    | "expiry_missing"
    | "parent_writeoff_missing"
    | "parent_writeoff_required"
    | "parent_writeoff_not_committed";
  message: string;
  /** Для `expiry_missing` — строки без срока. */
  itemIds: string[];
  /** Пункт не даёт провести документ. */
  blocking?: boolean;
}

export interface GoodsReceiptItemDto {
  id: string;
  receiptId: string;
  skuId: string;
  lotId: string | null;
  quantity: string;
  unitCost: string | null;
  salePrice: string | null;
  currency: string;
  batchNumber: string | null;
  manufacturedAt: string | null;
  expiresAt: string | null;
  datamatrixCodes: string[] | null;
  notes: string | null;
  /** phase19.14 (W14) — ячейка размещения строки приёмки («куда положили»). */
  binId: string | null;
  bin?: { id: string; code: string; label: string | null } | null;
  sku?: WmsSkuRef;
  lot?: {
    id: string;
    batchNumber: string;
    expiresAt: string | null;
    /** phase19.22 — код партии и признаки этикеток/срока. */
    code?: string;
    labelStatus?: LotLabelStatus;
    expiryRequired?: boolean;
  } | null;
  /** phase19.22 — номер строки: порядок печати этикеток и колонка «Строки». */
  lineNo?: number;
}

export interface WriteoffDto {
  id: string;
  shopId: string;
  warehouseId: string;
  reason: WriteoffReason;
  notes: string | null;
  writeoffNumber: string;
  status: "DRAFT" | "COMMITTED";
  committedAt: string | null;
  createdAt: string;
  warehouse?: { id: string; name: string };
  createdBy?: { id: string; displayName: string };
  items?: WriteoffItemDto[];
}

export interface WriteoffItemDto {
  id: string;
  writeoffId: string;
  skuId: string;
  lotId: string | null;
  quantity: string;
  notes: string | null;
  sku?: WmsSkuRef;
  /**
   * `lot.bin` — ячейка, из которой списали (phase19.23 этап A). Поле остаётся
   * опциональным: у партии может не быть адреса хранения, тогда UI пишет «—».
   */
  lot?: {
    id: string;
    batchNumber: string;
    expiresAt: string | null;
    bin?: { id: string; code: string; label: string | null } | null;
  } | null;
}

export interface ChannelReserveDto {
  id: string;
  channel: SalesChannelKind;
  skuId: string;
  warehouseId: string;
  reservedQuantity: string;
  safetyBuffer: string;
  sku: WmsSkuRef;
  warehouse: { id: string; name: string };
}

export interface StockMovementDto {
  id: string;
  shopId: string;
  skuId: string;
  /** Артикул на момент движения (снимок) — журнал читается и после смены кода. */
  skuCode: string;
  warehouseId: string;
  lotId: string | null;
  kind: StockMovementKind;
  quantity: string;
  refOrderId: string | null;
  refReceiptId: string | null;
  refWriteoffId: string | null;
  /** phase19.23 этап B — документ «Перемещение» (`MV-…`). */
  refTransferId: string | null;
  refReservationId: string | null;
  actorUserId: string;
  notes: string | null;
  createdAt: string;
  sku?: WmsSkuRef;
  warehouse?: { id: string; name: string; code: string };
  lot?: { id: string; batchNumber: string } | null;
  actor?: { id: string; displayName: string };
  /**
   * Документ-основание движения (phase19.23 этап A, зеркало
   * `movement-document.ts`). `null` — движение без документа (ручная
   * корректировка, резерв канала).
   */
  document?: StockMovementDocumentDto | null;
}

/** Вид документа-основания движения. */
export type StockMovementDocumentKind =
  | "RECEIPT"
  | "WRITEOFF"
  | "TRANSFER"
  | "ORDER";

/** Ссылка на документ-основание: вид, id и человеческий номер. */
export interface StockMovementDocumentDto {
  kind: StockMovementDocumentKind;
  id: string;
  number: string;
}

export interface StockReservationDto {
  id: string;
  orderId: string | null;
  channel: SalesChannelKind;
  skuId: string;
  warehouseId: string;
  lotId: string | null;
  quantity: string;
  status: StockReservationStatus;
  expiresAt: string;
  confirmedAt: string | null;
  fulfilledAt: string | null;
  releasedAt: string | null;
  releasedReason: string | null;
  createdAt: string;
  sku?: WmsSkuRef;
  warehouse?: { id: string; name: string };
  lot?: { id: string; batchNumber: string } | null;
}

/**
 * Конфликт «списываем зарезервированное» из 409 `writeoff_reserved_stock`
 * (`error.details.conflicts[]`). Количества сервер отдаёт числами.
 */
export interface WriteoffReservedConflict {
  skuId: string;
  skuCode: string;
  /** Готовая подпись сервера: «Чабрец горный (chabrec-gorny-50g)». */
  skuLabel: string;
  writeoffQuantity: number;
  physicalQuantity: number;
  reservedQuantity: number;
}

/** Код 409, требующий подтверждения кладовщика при проведении списания. */
export const WRITEOFF_RESERVED_STOCK_CODE = "writeoff_reserved_stock";

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const warehousesApi = {
  list: () => apiFetch<WarehouseDto[]>("/admin/wms/warehouses"),
  findOne: (id: string) => apiFetch<WarehouseDto>(`/admin/wms/warehouses/${id}`),
  create: (data: {
    name: string;
    code: string;
    address?: string;
    phone?: string;
    workingHours?: string;
    kind?: WarehouseKind;
    isSellable?: boolean;
    isDefault?: boolean;
    notes?: string;
  }) =>
    apiFetch<WarehouseDto>("/admin/wms/warehouses", {
      method: "POST",
      json: data,
    }),
  update: (id: string, data: Partial<{
    name: string;
    code: string;
    address: string | null;
    phone: string | null;
    workingHours: string | null;
    kind: WarehouseKind;
    isSellable: boolean;
    isDefault: boolean;
    isActive: boolean;
    notes: string | null;
  }>) =>
    apiFetch<WarehouseDto>(`/admin/wms/warehouses/${id}`, {
      method: "PATCH",
      json: data,
    }),
  updateSettings: (id: string, data: Partial<WarehouseSettingsDto>) =>
    apiFetch<WarehouseSettingsDto>(`/admin/wms/warehouses/${id}/settings`, {
      method: "PATCH",
      json: data,
    }),
  deactivate: (id: string) =>
    apiFetch<WarehouseDto>(`/admin/wms/warehouses/${id}`, { method: "DELETE" }),
};

export const devicesApi = {
  list: (warehouseId: string) =>
    apiFetch<DeviceConfigDto[]>(
      `/admin/wms/warehouses/${warehouseId}/devices`,
    ),
  create: (
    warehouseId: string,
    data: {
      kind: WmsDeviceKind;
      transport: WmsDeviceTransport;
      name: string;
      vendor?: string;
      modelName?: string;
      config?: Record<string, unknown>;
      isDefault?: boolean;
      notes?: string;
    },
  ) =>
    apiFetch<DeviceConfigDto>(
      `/admin/wms/warehouses/${warehouseId}/devices`,
      { method: "POST", json: data },
    ),
  update: (
    warehouseId: string,
    id: string,
    data: Partial<{
      name: string;
      vendor: string | null;
      modelName: string | null;
      transport: WmsDeviceTransport;
      config: Record<string, unknown>;
      isDefault: boolean;
      isActive: boolean;
      notes: string | null;
    }>,
  ) =>
    apiFetch<DeviceConfigDto>(
      `/admin/wms/warehouses/${warehouseId}/devices/${id}`,
      { method: "PATCH", json: data },
    ),
  remove: (warehouseId: string, id: string) =>
    apiFetch<void>(
      `/admin/wms/warehouses/${warehouseId}/devices/${id}`,
      { method: "DELETE" },
    ),
};

export const stockApi = {
  /** `skuId` — конкретная фасовка, `productId` — все фасовки товара. */
  list: async (params?: {
    warehouseId?: string;
    skuId?: string;
    productId?: string;
    onlyPositive?: boolean;
    /** Только склады, доступные для продажи (W8) — сервер умеет с phase19.14. */
    onlySellable?: boolean;
    /**
     * Только фасовки с остатком ниже минимального (phase19.24 этап 1, шаг 3 —
     * плитка дашборда «Ниже минимума»). Старый сервер параметр игнорирует и
     * отдаёт весь список.
     */
    belowMin?: boolean;
    /**
     * Только партии без адреса хранения (правка по ревью 2026-09-20). Раньше
     * отбор доводился на клиенте по `lot.bin`, и «Без места: 40» на большом
     * складе показывал лишь те строки, что попали в выдачу.
     */
    unplaced?: boolean;
    /** phase19.22 (W17-9c) — «Без этикеток» (NONE) / «Этикетки устарели» (STALE). */
    labelStatus?: "NONE" | "STALE";
    /** phase19.22 (W20) — партии «срок не указан». */
    expiryMissing?: boolean;
  }): Promise<StockLevelsResult> => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.skuId) q.set("skuId", params.skuId);
    if (params?.productId) q.set("productId", params.productId);
    if (params?.onlyPositive) q.set("onlyPositive", "true");
    if (params?.onlySellable) q.set("onlySellable", "true");
    if (params?.belowMin) q.set("belowMin", "true");
    if (params?.unplaced) q.set("unplaced", "true");
    if (params?.labelStatus) q.set("labelStatus", params.labelStatus);
    if (params?.expiryMissing) q.set("expiryMissing", "true");
    const qs = q.toString();
    const { data, meta } = await apiFetchWithMeta<
      StockLevelDto[],
      { truncated?: boolean } | undefined
    >(`/admin/wms/stock${qs ? `?${qs}` : ""}`);
    return { items: data ?? [], truncated: meta?.truncated === true };
  },
  available: (params: {
    skuId: string;
    warehouseId: string;
    channel: SalesChannelKind;
  }) => {
    const q = new URLSearchParams({
      skuId: params.skuId,
      warehouseId: params.warehouseId,
      channel: params.channel,
    });
    return apiFetch<{ quantity: number }>(
      `/admin/wms/stock/available?${q.toString()}`,
    );
  },
  /**
   * Журнал движений. `refReceiptId` / `refWriteoffId` / `refOrderId` — выборка
   * по документу-источнику (phase19.23 этап A). Старый сервер эти параметры
   * игнорирует, поэтому вызывающий код обязан оставлять клиентскую доводку.
   */
  movements: (params?: {
    warehouseId?: string;
    skuId?: string;
    productId?: string;
    kind?: StockMovementKind;
    from?: string;
    to?: string;
    limit?: number;
    refReceiptId?: string;
    refWriteoffId?: string;
    refOrderId?: string;
    /** phase19.23 этап B — движения одного перемещения. */
    refTransferId?: string;
  }) => {
    const q = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null) q.set(k, String(v));
    });
    const qs = q.toString();
    return apiFetch<StockMovementDto[]>(
      `/admin/wms/movements${qs ? `?${qs}` : ""}`,
    );
  },
  reservations: (params?: { orderId?: string; status?: StockReservationStatus }) => {
    const q = new URLSearchParams();
    if (params?.orderId) q.set("orderId", params.orderId);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return apiFetch<StockReservationDto[]>(
      `/admin/wms/reservations${qs ? `?${qs}` : ""}`,
    );
  },
};

export const lotsApi = {
  /** `skuId` — партии одной фасовки, `productId` — по всем фасовкам товара. */
  list: (params?: { skuId?: string; productId?: string; supplierId?: string }) => {
    const q = new URLSearchParams();
    if (params?.skuId) q.set("skuId", params.skuId);
    if (params?.productId) q.set("productId", params.productId);
    if (params?.supplierId) q.set("supplierId", params.supplierId);
    const qs = q.toString();
    return apiFetch<LotDto[]>(`/admin/wms/lots${qs ? `?${qs}` : ""}`);
  },
  expiring: (params?: { alertDays?: number; warehouseId?: string }) => {
    const q = new URLSearchParams();
    if (params?.alertDays !== undefined) q.set("alertDays", String(params.alertDays));
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    const qs = q.toString();
    return apiFetch<LotDto[]>(`/admin/wms/lots/expiring${qs ? `?${qs}` : ""}`);
  },
  findOne: (id: string) => apiFetch<LotDto>(`/admin/wms/lots/${id}`),
  update: (id: string, data: Partial<LotDto>) =>
    apiFetch<LotDto>(`/admin/wms/lots/${id}`, { method: "PATCH", json: data }),
};

/**
 * Страница списка складских документов (phase19.23 этап A).
 * Сервер отдаёт `{ data, meta: { total, page, pageSize } }`; `meta` может не
 * прийти со старого сервера — тогда считаем, что страница одна.
 */
export interface WmsDocumentsPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Общий разбор конверта списка документов — один на приёмки и списания. */
async function fetchDocumentsPage<T>(
  path: string,
  q: URLSearchParams,
  fallbackPage: number,
  fallbackPageSize: number,
): Promise<WmsDocumentsPage<T>> {
  const qs = q.toString();
  const { data, meta } = await apiFetchWithMeta<
    T[],
    { total: number; page: number; pageSize: number } | undefined
  >(`${path}${qs ? `?${qs}` : ""}`);
  return {
    items: data ?? [],
    total: meta?.total ?? data?.length ?? 0,
    page: meta?.page ?? fallbackPage,
    pageSize: meta?.pageSize ?? fallbackPageSize,
  };
}

/** Фильтры списка документов; поиск и пагинация считаются на сервере. */
export interface WmsDocumentsListParams {
  warehouseId?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const receiptsApi = {
  list: (params?: { warehouseId?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return apiFetch<GoodsReceiptDto[]>(`/admin/wms/receipts${qs ? `?${qs}` : ""}`);
  },
  /** Список с серверными фильтрами и пагинацией — экран «Приёмки». */
  listPage: (params: WmsDocumentsListParams) => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const q = new URLSearchParams();
    if (params.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params.status) q.set("status", params.status);
    if (params.search) q.set("search", params.search);
    q.set("page", String(page));
    q.set("pageSize", String(pageSize));
    return fetchDocumentsPage<GoodsReceiptDto>(
      "/admin/wms/receipts",
      q,
      page,
      pageSize,
    );
  },
  findOne: (id: string) => apiFetch<GoodsReceiptDto>(`/admin/wms/receipts/${id}`),
  create: (data: {
    warehouseId: string;
    supplierId?: string;
    vendorDocNumber?: string;
    vendorDocDate?: string;
    notes?: string;
    reason?: GoodsReceiptReason;
    parentWriteoffId?: string | null;
  }) =>
    apiFetch<GoodsReceiptDto>("/admin/wms/receipts", {
      method: "POST",
      json: data,
    }),
  /** phase19.22 — правка шапки черновика; `null` — очистить поле. */
  update: (
    id: string,
    data: Partial<{
      supplierId: string | null;
      vendorDocNumber: string | null;
      vendorDocDate: string | null;
      notes: string | null;
      reason: GoodsReceiptReason;
      parentWriteoffId: string | null;
    }>,
  ) =>
    apiFetch<GoodsReceiptDto>(`/admin/wms/receipts/${id}`, { method: "PATCH", json: data }),
  /** phase19.22 (W20) — предупреждения проведения ДО «Провести». */
  commitCheck: (id: string) =>
    apiFetch<{ warnings: ReceiptWarning[]; errors?: ReceiptWarning[] }>(`/admin/wms/receipts/${id}/commit-check`),
  /**
   * phase19.22 (W20 «Указать срок») — только проведённая приёмка и только
   * «пусто → значение»; этикетки партии становятся устаревшими.
   */
  setItemExpiry: (receiptId: string, itemId: string, expiresAt: string) =>
    apiFetch<{ itemId: string; hiddenPrintJobs: number }>(
      `/admin/wms/receipts/${receiptId}/items/${itemId}/expiry`,
      { method: "PATCH", json: { expiresAt } },
    ),
  addItem: (
    receiptId: string,
    data: {
      skuId: string;
      quantity: string;
      unitCost?: string;
      salePrice?: string;
      currency?: string;
      batchNumber?: string;
      manufacturedAt?: string;
      expiresAt?: string;
      notes?: string;
      /**
       * phase19.14 (W14) — ячейка размещения. Пусто → при проведении партия
       * унаследует ячейку по умолчанию у фасовки. Поменять её в черновике —
       * `updateItem` (phase19.22).
       */
      binId?: string | null;
    },
  ) =>
    apiFetch<GoodsReceiptItemDto>(
      `/admin/wms/receipts/${receiptId}/items`,
      { method: "POST", json: data },
    ),
  /**
   * phase19.22 — правка строки ЧЕРНОВИКА (`PATCH …/items/:itemId`):
   * `undefined` — не трогать, `null` — очистить. Срок сервер считает сам из
   * даты изготовления, если он `null` или изменилась дата у строки без срока.
   * Проведённая приёмка — 409 `receipt_not_draft` (там «Указать срок»).
   *
   * phase19.31 (контракт §15): цены строки — `unitCost`/`salePrice` деньгами
   * строкой (запятая допустима), `null` — очистить; отрицательное или не
   * число — 422 `receipt_item_price_invalid` (`details.field`). `salePrice`
   * кладовщика сервер молча не сохраняет (§11).
   */
  updateItem: (
    receiptId: string,
    itemId: string,
    data: Partial<{
      quantity: string;
      unitCost: string | null;
      salePrice: string | null;
      batchNumber: string | null;
      manufacturedAt: string | null;
      expiresAt: string | null;
      binId: string | null;
    }>,
  ) =>
    apiFetch<GoodsReceiptItemDto>(`/admin/wms/receipts/${receiptId}/items/${itemId}`, {
      method: "PATCH",
      json: data,
    }),
  removeItem: (receiptId: string, itemId: string) =>
    apiFetch<void>(
      `/admin/wms/receipts/${receiptId}/items/${itemId}`,
      { method: "DELETE" },
    ),
  commit: (receiptId: string) =>
    apiFetch<GoodsReceiptDto>(`/admin/wms/receipts/${receiptId}/commit`, {
      method: "POST",
    }),
};

export const writeoffsApi = {
  list: (params?: { warehouseId?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return apiFetch<WriteoffDto[]>(`/admin/wms/writeoffs${qs ? `?${qs}` : ""}`);
  },
  /** Список с серверными фильтрами и пагинацией — экран «Списания». */
  listPage: (params: WmsDocumentsListParams & { reason?: string }) => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const q = new URLSearchParams();
    if (params.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params.status) q.set("status", params.status);
    if (params.search) q.set("search", params.search);
    if (params.reason) q.set("reason", params.reason);
    q.set("page", String(page));
    q.set("pageSize", String(pageSize));
    return fetchDocumentsPage<WriteoffDto>(
      "/admin/wms/writeoffs",
      q,
      page,
      pageSize,
    );
  },
  findOne: (id: string) => apiFetch<WriteoffDto>(`/admin/wms/writeoffs/${id}`),
  create: (data: {
    warehouseId: string;
    reason: WriteoffReason;
    notes?: string;
  }) =>
    apiFetch<WriteoffDto>("/admin/wms/writeoffs", {
      method: "POST",
      json: data,
    }),
  addItem: (
    writeoffId: string,
    data: { skuId: string; lotId?: string; quantity: string; notes?: string },
  ) =>
    apiFetch<WriteoffItemDto>(`/admin/wms/writeoffs/${writeoffId}/items`, {
      method: "POST",
      json: data,
    }),
  removeItem: (writeoffId: string, itemId: string) =>
    apiFetch<void>(
      `/admin/wms/writeoffs/${writeoffId}/items/${itemId}`,
      { method: "DELETE" },
    ),
  /**
   * Проведение списания. `confirmReservedWriteoff: true` — повтор после 409
   * `writeoff_reserved_stock` (кладовщик подтвердил списание брони).
   */
  commit: (writeoffId: string, options?: { confirmReservedWriteoff?: boolean }) =>
    apiFetch<WriteoffDto>(`/admin/wms/writeoffs/${writeoffId}/commit`, {
      method: "POST",
      json: { confirmReservedWriteoff: options?.confirmReservedWriteoff ?? false },
    }),
  /**
   * Акт списания (PDF). Сервер отдаёт его только по проведённому документу
   * (409 `writeoff_not_committed`) — черновик ещё ничего не списал.
   */
  printAct: (id: string, number: string): Promise<ApiFileResponse> =>
    apiDownload(
      `/admin/wms/writeoffs/${id}/act.pdf`,
      { headers: { Accept: "application/pdf" } },
      `writeoff-act-${number}.pdf`,
    ),
};

export const channelReservesApi = {
  list: (params?: { warehouseId?: string; channel?: SalesChannelKind }) => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.channel) q.set("channel", params.channel);
    const qs = q.toString();
    return apiFetch<ChannelReserveDto[]>(
      `/admin/wms/channel-reserves${qs ? `?${qs}` : ""}`,
    );
  },
  setSafetyBuffer: (data: {
    channel: SalesChannelKind;
    skuId: string;
    warehouseId: string;
    safetyBuffer: string;
  }) =>
    apiFetch<ChannelReserveDto>(
      "/admin/wms/channel-reserves/safety-buffer",
      { method: "POST", json: data },
    ),
};

// ─── Пикер фасовок склада ─────────────────────────────────────────────────────

/**
 * Строка пикера фасовок — контракт `GET /admin/wms/skus` (phase19.13).
 *
 * Отдельный от заказного пикера эндпоинт: кладовщику не выдают права на заказы
 * и не показывают цены. `displayName` сервер уже свёл к «имя фасовки, иначе имя
 * товара», поэтому оно может совпадать с `productName`.
 */
export interface WmsSkuPickerItem {
  id: string;
  code: string;
  displayName: string;
  productId: string;
  productName: string;
  saleQuantity: string;
  saleUnit: string | null;
  gtin: string | null;
  /** Товар учитывается партиями — при приёмке спрашиваем партию и срок. */
  trackByLot: boolean;
  /** false — фасовка выведена из оборота (видна только с includeArchived). */
  isActive: boolean;
  /** phase19.22 (W17-1) — код склада `CHB…`; скан этикетки ищет по нему. */
  internalCode?: string;
}

export interface WmsSkuPickerPage {
  items: WmsSkuPickerItem[];
  /** Всего строк под текущим фильтром — для пагинации/подписи «показаны N из M». */
  total: number;
}

export const wmsSkusApi = {
  /** Поиск фасовок для складских документов (право SHOP_WMS_VIEW). */
  search: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set("search", params.search);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.offset !== undefined) q.set("offset", String(params.offset));
    if (params?.includeArchived) q.set("includeArchived", "true");
    const qs = q.toString();
    return apiFetch<WmsSkuPickerPage>(`/admin/wms/skus${qs ? `?${qs}` : ""}`);
  },
};

/**
 * Подпись позиции в пикере: «Чабрец горный — 50 г».
 *
 * Сервер подставляет в `displayName` название товара, когда у фасовки своего
 * имени нет, — тогда тире с дублем не рисуем.
 */
export function formatPickerSkuName(sku: WmsSkuPickerItem): string {
  const variant = sku.displayName.trim();
  const product = sku.productName.trim();
  if (!variant || variant === product) return product || sku.code;
  return `${product} — ${variant}`;
}

// ─── SKU helpers ──────────────────────────────────────────────────────────────

/**
 * Название позиции без артикула: «Чабрец горный — 50 г».
 *
 * Название товара берём с карточки, через тире — имя фасовки (если задано).
 * Если карточка не пришла в выборке, показываем артикул: пустая строка на
 * складе хуже кода.
 */
export function formatSkuName(sku?: WmsSkuRef | null): string {
  if (!sku) return "—";
  const product = sku.product?.displayName?.trim();
  const variant = sku.displayName?.trim();
  if (product && variant) return `${product} — ${variant}`;
  return product || variant || sku.code;
}

/** Полная подпись позиции: «Чабрец горный — 50 г (chabrec-gorny-50g)». */
export function formatSkuLabel(sku?: WmsSkuRef | null): string {
  if (!sku) return "—";
  const name = formatSkuName(sku);
  return name === sku.code ? sku.code : `${name} (${sku.code})`;
}

/**
 * Разбирает `error.details` из 409 `writeoff_reserved_stock`.
 * Возвращает пустой массив, если сервер прислал неожиданную структуру, —
 * диалог подтверждения тогда не показываем и падаем на обычный toast.
 */
export function parseReservedConflicts(details: unknown): WriteoffReservedConflict[] {
  if (typeof details !== "object" || details === null) return [];
  const raw = (details as { conflicts?: unknown }).conflicts;
  if (!Array.isArray(raw)) return [];
  const result: WriteoffReservedConflict[] = [];
  for (const row of raw) {
    if (typeof row !== "object" || row === null) continue;
    const c = row as Record<string, unknown>;
    if (typeof c.skuId !== "string") continue;
    const skuCode = typeof c.skuCode === "string" ? c.skuCode : "";
    result.push({
      skuId: c.skuId,
      skuCode,
      skuLabel: typeof c.skuLabel === "string" ? c.skuLabel : skuCode || c.skuId,
      writeoffQuantity: Number(c.writeoffQuantity ?? 0),
      physicalQuantity: Number(c.physicalQuantity ?? 0),
      reservedQuantity: Number(c.reservedQuantity ?? 0),
    });
  }
  return result;
}

// ─── Labels (RU) ───────────────────────────────────────────────────────────────

export const SALES_CHANNEL_LABEL: Record<SalesChannelKind, string> = {
  RETAIL: "Розница",
  WEBSITE: "Сайт",
  OZON: "Ozon",
  WILDBERRIES: "Wildberries",
  AVITO: "Avito",
  B2B: "B2B",
};

export const WRITEOFF_REASON_LABEL: Record<WriteoffReason, string> = {
  DAMAGED: "Повреждён",
  EXPIRED: "Просрочен",
  LOST: "Потерян",
  RETURN_TO_VENDOR: "Возврат поставщику",
  PRODUCTION_INPUT: "В производство",
  OTHER: "Другое",
};

/** Причины приёмки (W17-9b) — данными: новая причина = запись здесь. */
export const RECEIPT_REASON_LABEL: Record<GoodsReceiptReason, string> = {
  PURCHASE: "Покупка",
  INTERNAL_OUTPUT: "Внутренний выпуск",
  OPENING_BALANCE: "Начальные остатки",
};

export const DEVICE_KIND_LABEL: Record<WmsDeviceKind, string> = {
  PRINTER_LABEL: "Принтер этикеток",
  PRINTER_RECEIPT: "Принтер чеков",
  PRINTER_DOCUMENT: "Принтер документов A4",
  SCANNER_USB: "USB-сканер",
  SCANNER_CAMERA: "Камера-сканер",
  SCALE: "Весы",
  CASH_REGISTER: "Касса",
  PAYMENT_TERMINAL: "Терминал эквайринга",
  POS_TERMINAL: "POS-терминал",
  OTHER: "Другое",
};

export const DEVICE_TRANSPORT_LABEL: Record<WmsDeviceTransport, string> = {
  USB: "USB",
  NETWORK: "Сеть (IP)",
  AGENT: "Локальный агент",
  BLUETOOTH: "Bluetooth",
  SYSTEM: "Системный (ОС)",
  CLOUD_API: "Облачный API",
};

export const MOVEMENT_KIND_LABEL: Record<StockMovementKind, string> = {
  RECEIVE: "Приёмка",
  SALE: "Продажа",
  RESERVE: "Резерв",
  UNRESERVE: "Снятие резерва",
  WRITEOFF: "Списание",
  TRANSFER_IN: "Перемещение (вход)",
  TRANSFER_OUT: "Перемещение (выход)",
  RETURN_TO_STOCK: "Возврат на склад",
  ADJUST: "Корректировка",
  PRODUCTION_CONSUMPTION: "Производство (расход)",
  PRODUCTION_OUTPUT: "Производство (выпуск)",
};
